/**
 * Create Pending Member Screen
 * Add a new family member to pre-register
 */

import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Switch,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { usePendingMembers } from "@/modules/pending-members";
import DateTimePicker from "@react-native-community/datetimepicker";

interface Team {
  id: string;
  name: string;
  color: string;
}

export default function CreatePendingMemberScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend } = useAuth();
  const { createPendingMember } = usePendingMembers();

  // Form state
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [nickname, setNickname] = useState("");
  const [phone, setPhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [isCaptain, setIsCaptain] = useState(false);
  
  // Teams
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  
  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      if (!activeWeekend?.id) {
        setIsLoadingTeams(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("teams")
          .select("id, name, color")
          .eq("weekend_id", activeWeekend.id)
          .order("name");

        if (error) throw error;
        setTeams(data || []);
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setIsLoadingTeams(false);
      }
    };

    fetchTeams();
  }, [activeWeekend?.id]);

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    // Validation
    if (!email.trim()) {
      Alert.alert("Fout", "E-mailadres is verplicht");
      return;
    }
    if (!fullName.trim()) {
      Alert.alert("Fout", "Naam is verplicht");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      Alert.alert("Fout", "Voer een geldig e-mailadres in");
      return;
    }

    setIsSubmitting(true);

    try {
      const success = await createPendingMember({
        email: email.trim().toLowerCase(),
        full_name: fullName.trim(),
        nickname: nickname.trim() || undefined,
        phone: phone.trim() || undefined,
        date_of_birth: dateOfBirth?.toISOString().split("T")[0],
        team_id: teamId || undefined,
        is_captain: isCaptain,
        weekend_id: activeWeekend?.id,
      });

      if (success) {
        Alert.alert(
          "✅ Toegevoegd",
          `${fullName} is toegevoegd als pre-registratie`,
          [{ text: "OK", onPress: () => router.back() }]
        );
      }
    } catch (error: any) {
      Alert.alert("Fout", error.message || "Kon lid niet toevoegen");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={{ flex: 1, backgroundColor: colors.background }}
    >
      <ScrollView className="flex-1 px-4 py-4">
        {/* Email */}
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            E-mailadres *
          </Text>
          <TextInput
            value={email}
            onChangeText={setEmail}
            placeholder="familie@voorbeeld.nl"
            placeholderTextColor={colors.textTertiary}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            className="rounded-xl px-4 py-3"
            style={{
              backgroundColor: colors.surface,
              color: colors.text,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          />
          <Text className="text-xs mt-1" style={{ color: colors.textSecondary }}>
            Gebruiker moet aanmelden met dit exacte e-mailadres
          </Text>
        </View>

        {/* Full Name */}
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            Volledige naam *
          </Text>
          <TextInput
            value={fullName}
            onChangeText={setFullName}
            placeholder="Peter Staats"
            placeholderTextColor={colors.textTertiary}
            autoCapitalize="words"
            className="rounded-xl px-4 py-3"
            style={{
              backgroundColor: colors.surface,
              color: colors.text,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          />
        </View>

        {/* Nickname */}
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            Bijnaam
          </Text>
          <TextInput
            value={nickname}
            onChangeText={setNickname}
            placeholder="Opa Peter"
            placeholderTextColor={colors.textTertiary}
            autoCapitalize="words"
            className="rounded-xl px-4 py-3"
            style={{
              backgroundColor: colors.surface,
              color: colors.text,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          />
        </View>

        {/* Phone */}
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            Telefoonnummer
          </Text>
          <TextInput
            value={phone}
            onChangeText={setPhone}
            placeholder="+31 6 12345678"
            placeholderTextColor={colors.textTertiary}
            keyboardType="phone-pad"
            className="rounded-xl px-4 py-3"
            style={{
              backgroundColor: colors.surface,
              color: colors.text,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          />
        </View>

        {/* Date of Birth */}
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            Geboortedatum
          </Text>
          <TouchableOpacity
            onPress={() => setShowDatePicker(true)}
            className="rounded-xl px-4 py-3 flex-row items-center justify-between"
            style={{
              backgroundColor: colors.surface,
              borderWidth: 1,
              borderColor: colors.border,
            }}
          >
            <Text style={{ color: dateOfBirth ? colors.text : colors.textTertiary }}>
              {dateOfBirth
                ? dateOfBirth.toLocaleDateString("nl-NL", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : "Selecteer datum"}
            </Text>
            <Ionicons name="calendar-outline" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {showDatePicker && (
          <DateTimePicker
            value={dateOfBirth || new Date(1990, 0, 1)}
            mode="date"
            display={Platform.OS === "ios" ? "spinner" : "default"}
            onChange={(event, selectedDate) => {
              setShowDatePicker(Platform.OS === "ios");
              if (selectedDate) {
                setDateOfBirth(selectedDate);
              }
            }}
            maximumDate={new Date()}
          />
        )}

        {/* Team Selection */}
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
            Team
          </Text>
          {isLoadingTeams ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : teams.length === 0 ? (
            <Text style={{ color: colors.textSecondary }}>
              Geen teams gevonden. Maak eerst teams aan.
            </Text>
          ) : (
            <View className="flex-row flex-wrap gap-2">
              <TouchableOpacity
                onPress={() => setTeamId(null)}
                className="rounded-xl px-4 py-2"
                style={{
                  backgroundColor: teamId === null ? colors.primary + "20" : colors.surface,
                  borderWidth: teamId === null ? 2 : 1,
                  borderColor: teamId === null ? colors.primary : colors.border,
                }}
              >
                <Text style={{ color: teamId === null ? colors.primary : colors.textSecondary }}>
                  Geen team
                </Text>
              </TouchableOpacity>
              {teams.map((team) => (
                <TouchableOpacity
                  key={team.id}
                  onPress={() => setTeamId(team.id)}
                  className="rounded-xl px-4 py-2 flex-row items-center"
                  style={{
                    backgroundColor: teamId === team.id ? team.color + "30" : colors.surface,
                    borderWidth: teamId === team.id ? 2 : 1,
                    borderColor: teamId === team.id ? team.color : colors.border,
                  }}
                >
                  <View
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: team.color }}
                  />
                  <Text style={{ color: teamId === team.id ? team.color : colors.text }}>
                    {team.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>

        {/* Captain Toggle */}
        {teamId && (
          <View className="mb-6">
            <View
              className="flex-row items-center justify-between rounded-xl px-4 py-3"
              style={{ backgroundColor: colors.surface }}
            >
              <View>
                <Text className="font-medium" style={{ color: colors.text }}>
                  Teamkapitein
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Maak dit lid kapitein van het team
                </Text>
              </View>
              <Switch
                value={isCaptain}
                onValueChange={setIsCaptain}
                trackColor={{ false: colors.border, true: colors.primary + "80" }}
                thumbColor={isCaptain ? colors.primary : colors.surface}
              />
            </View>
          </View>
        )}

        {/* Submit Button */}
        <TouchableOpacity
          onPress={handleSubmit}
          disabled={isSubmitting}
          className="rounded-xl py-4 items-center mb-8"
          style={{
            backgroundColor: isSubmitting ? colors.textSecondary : "#DC2626",
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text className="text-white font-semibold text-base">
              Familielid Toevoegen
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

